import { Link } from '@inertiajs/react';
import React, { useState, useEffect } from 'react';
import { Page, Event } from '../types';
import { EventCard } from '../components/EventCard';
import { FeatureCard } from '../components/FeatureCard';
import { TestimonialCard } from '../components/TestimonialCard';
import { Head } from '@inertiajs/react';
import { Code, Users, Briefcase, ArrowRight, ArrowUpRight } from 'lucide-react';
import python2025_2e_group_picture from '../../assets/images/python_group_picture_2e.jpg';
import nyaLogo from '../../assets/images/NYA.jpeg';
import nexstepLogo from '../../assets/images/nexstep.png';
import heroImg1 from '../../assets/images/PHOTO-2026-01-04-16-12-25.jpg';
import heroImg2 from '../../assets/images/PHOTO-2026-01-04-16-12-25_1.jpg';
import heroImg3 from '../../assets/images/PHOTO-2026-01-04-16-12-48.jpg';
import heroImg4 from '../../assets/images/PHOTO-2026-01-04-16-12-52.jpg';
import heroImg5 from '../../assets/images/PHOTO-2026-01-04-16-12-56.jpg';
import heroImg6 from '../../assets/images/PHOTO-2026-01-04-16-12-56_1.jpg';
import heroImg7 from '../../assets/images/PHOTO-2026-01-04-16-12-57.jpg';
import heroImg8 from '../../assets/images/PHOTO-2026-01-04-16-12-58.jpg';
import heroImg9 from '../../assets/images/PHOTO-2026-01-04-16-12-59.jpg';
import heroImg10 from '../../assets/images/PHOTO-2026-01-04-16-12-59_1.jpg';
import heroImg11 from '../../assets/images/PHOTO-2026-01-04-16-13-00.jpg';
import heroImg12 from '../../assets/images/PHOTO-2026-01-04-16-13-01.jpg';
import heroImg13 from '../../assets/images/PHOTO-2026-01-04-16-13-01_1.jpg';
import heroImg14 from '../../assets/images/PHOTO-2026-01-04-16-13-02.jpg';
import heroImg15 from '../../assets/images/PHOTO-2026-01-04-16-13-02_1.jpg';

interface HomePageProps {
  upcoming_events: Event[];
}

const HomePage: React.FC<HomePageProps> = ({ upcoming_events }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slideImages = [
    { url: python2025_2e_group_picture },
    { url: heroImg1 },
    { url: heroImg2 },
    { url: heroImg3 },
    { url: heroImg4 },
    { url: heroImg5 },
    { url: heroImg6 },
    { url: heroImg7 },
    { url: heroImg8 },
    { url: heroImg9 },
    { url: heroImg10 },
    { url: heroImg11 },
    { url: heroImg12 },
    { url: heroImg13 },
    { url: heroImg14 },
    { url: heroImg15 },
    { url: 'https://images.unsplash.com/photo-1528901166007-3784c7dd3653?q=80&w=2070&auto=format&fit=crop' },
    { url: 'https://images.unsplash.com/photo-1543269865-cbf427effbad?q=80&w=2070&auto=format&fit=crop' },
    { url: 'https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2070&auto=format&fit=crop' },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % slideImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slideImages.length]);

  return (
    <div>
      <section className='relative min-h-screen flex items-center justify-center text-center overflow-hidden'>
        <div className='absolute inset-0 z-0'>
          {slideImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ backgroundImage: `url('${image.url}')` }}
              aria-hidden={index !== currentSlide}
            ></div>
          ))}
        </div>

        <div aria-hidden='true' className='absolute inset-0 bg-black/70 z-10'></div>

        <div className='container mx-auto px-6 relative z-20'>
          <p
            className='inline-flex items-center gap-2.5 text-sm md:text-base font-bold tracking-widest text-slate-200 mb-4 uppercase opacity-0 animate-fade-in-up'
            style={{ animationDelay: '0.1s' }}
          >
            <span className='h-1.5 w-1.5 rounded-full bg-brand-orange shadow-[0_0_8px_var(--color-brand-orange)]'></span>
            A Program of the NexStep Foundation Project
          </p>
          <h1
            className='text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 text-white max-w-5xl mx-auto opacity-0 animate-fade-in-up font-heading leading-tight'
            style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)', animationDelay: '0.3s' }}
          >
            Empowering <span className='text-primary'>Africa's</span> Future Through{' '}
            <span className='text-brand-orange'>Digital</span> Education.
          </h1>
          <p
            className='text-lg md:text-xl max-w-3xl mx-auto mb-8 text-slate-200 opacity-0 animate-fade-in-up'
            style={{ animationDelay: '0.5s' }}
          >
            Equipping the youth in IT disciplines by organizing regular workshops and providing platforms for networking
            and future opportunities.
          </p>
          <div
            className='flex flex-col sm:flex-row items-center justify-center gap-4 opacity-0 animate-fade-in-up'
            style={{ animationDelay: '0.7s' }}
          >
            <Link
              href='/events'
              className='bg-primary text-primary-foreground font-bold px-8 py-4 rounded-full text-lg hover:bg-primary/90 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-primary/30'
            >
              Explore Our Events
            </Link>
            <Link
              href='/support'
              className='bg-secondary text-secondary-foreground font-bold px-8 py-4 rounded-full text-lg hover:bg-secondary/90 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-secondary/30'
            >
              Support Our Cause
            </Link>
          </div>
        </div>

        {/* Desktop: discrete dots — there's room for them on wide screens */}
        <div className='hidden md:flex absolute bottom-10 left-1/2 -translate-x-1/2 z-20 space-x-3'>
          {slideImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
              className={`w-3 h-3 rounded-full transition-colors ${
                currentSlide === index ? 'bg-white' : 'bg-white/50 hover:bg-white/75'
              }`}
            ></button>
          ))}
        </div>
      </section>

      <section className='py-20 bg-muted'>
        <div className='container mx-auto px-6 text-center'>
          <p className='text-sm font-bold tracking-widest text-secondary uppercase mb-3 animate-fade-in'>
            What We Do
          </p>
          <h2 className='text-3xl md:text-4xl font-bold text-foreground mb-4 font-heading animate-fade-in'>
            Digital Evangelism & <span className='text-secondary'>Inclusion</span>
          </h2>
          <p className='text-lg text-muted-foreground max-w-2xl mx-auto mb-12 animate-fade-in'>
            We focus on digital, science, and technology inclusion in Ghana and Africa to enhance standards of living
            and alleviate poverty through education.
          </p>
          <div className='grid md:grid-cols-3 gap-8'>
            <div className='opacity-0 animate-fade-in-up' style={{ animationDelay: '0.2s' }}>
              <FeatureCard
                accent='primary'
                icon={<Code className='h-8 w-8' />}
                title='Training & Workshops'
                description='From Microsoft Office (Excel, Access) to Programming. We offer beginner-friendly, hands-on sessions to upskill our community.'
              />
            </div>
            <div className='opacity-0 animate-fade-in-up' style={{ animationDelay: '0.4s' }}>
              <FeatureCard
                accent='secondary'
                icon={<Users className='h-8 w-8' />}
                title='Mentorship & Community'
                description='Join our supportive community where learning continues, resources are shared, and peers connect.'
              />
            </div>
            <div className='opacity-0 animate-fade-in-up' style={{ animationDelay: '0.6s' }}>
              <FeatureCard
                accent='orange'
                icon={<Briefcase className='h-8 w-8' />}
                title='Career Development'
                description='We create platforms for networking and future opportunities, enhancing the standard of living for our participants.'
              />
            </div>
          </div>
        </div>
      </section>

      {upcoming_events.length > 0 && (
        <section id='upcoming-events' className='py-20 bg-background'>
          <div className='container mx-auto px-6'>
            <div className='text-center mb-12'>
              <h2 className='text-3xl md:text-4xl font-bold text-foreground font-heading'>Upcoming Events</h2>
              <p className='text-lg text-muted-foreground mt-2'>
                Join us for our next series of workshops and meetups.
              </p>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
              {upcoming_events.map((event, index) => (
                <div
                  key={event.id}
                  className='opacity-0 animate-fade-in-up'
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <EventCard event={event} isUpcoming={true} />
                </div>
              ))}
            </div>
            <div className='mt-12 text-center'>
              <Link
                href='/events'
                className='text-lg font-semibold text-primary hover:text-primary/80 transition-colors inline-flex items-center'
              >
                View all events <ArrowRight className='ml-2 h-5 w-5' />
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className='py-20'>
        <div className='container mx-auto px-6 text-center'>
          <h2 className='text-3xl md:text-4xl font-bold text-foreground mb-4 font-heading'>Our Partners & Sponsors</h2>
          <p className='text-lg text-muted-foreground max-w-2xl mx-auto mb-12'>
            We are proud to collaborate with organizations that share our vision for a digitally empowered future.
          </p>
          <div
            className='grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto mb-12 text-left opacity-0 animate-fade-in'
            style={{ animationDelay: '0.3s' }}
          >
            {[
              {
                name: 'NexStep Foundation',
                logo: nexstepLogo,
                description:
                  'The parent foundation behind Digital Wave, expanding access to digital skills and opportunity across Africa.',
                website: undefined as string | undefined,
              },
              {
                name: 'National Youth Authority',
                logo: nyaLogo,
                description:
                  "Ghana's state agency facilitating youth development and empowerment nationwide.",
                website: 'https://nya.gov.gh',
              },
            ].map((partner) => (
              <div
                key={partner.name}
                className='group bg-card border border-border rounded-2xl p-6 hover:shadow-lg hover:border-primary/30 transition-all duration-300'
              >
                <div className='h-12 flex items-center mb-5'>
                  <img src={partner.logo} alt={partner.name} className='max-h-12 w-auto object-contain' />
                </div>
                <h3 className='text-lg font-bold text-foreground font-heading mb-2'>{partner.name}</h3>
                <p className='text-sm text-muted-foreground mb-4 leading-relaxed'>{partner.description}</p>
                {partner.website && (
                  <a
                    href={partner.website}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80 transition-colors'
                  >
                    Visit website <ArrowUpRight className='h-4 w-4' />
                  </a>
                )}
              </div>
            ))}
          </div>
          <p className='text-muted-foreground mb-6'>Interested in shaping the future of tech with us?</p>
          <Link
            href='/contact'
            className='bg-primary text-primary-foreground font-bold px-8 py-3 rounded-full text-lg hover:bg-primary/90 transition-all duration-300 transform hover:scale-105'
          >
            Become a Partner
          </Link>
        </div>
      </section>

      <section className='py-20 bg-muted'>
        <div className='container mx-auto px-6'>
          <p className='text-sm font-bold tracking-widest text-secondary uppercase text-center mb-3'>
            Real Impact
          </p>
          <h2 className='text-3xl md:text-4xl font-bold text-foreground text-center mb-12 font-heading'>
            Success Stories
          </h2>
          <div className='grid md:grid-cols-2 gap-8 max-w-4xl mx-auto'>
            <div className='opacity-0 animate-scale-in' style={{ animationDelay: '0.2s' }}>
              <TestimonialCard
                quote="As a lecturer at GIMPA, the Digital Wave program enhanced my efficiency at my job significantly. It's truly productive."
                name='Nana Atuobi Yiadom'
                title='Lecturer, GIMPA'
                avatar='https://placehold.co/100x100?text=NY'
              />
            </div>
            <div className='opacity-0 animate-scale-in' style={{ animationDelay: '0.4s' }}>
              <TestimonialCard
                quote="Thanks to Digital Wave's sponsorship, I am now pursuing an IT discipline at the University of Ghana, Legon. This program changed my life."
                name='Bismark Mansro'
                title='Student, University of Ghana'
                avatar='https://placehold.co/100x100?text=BM'
              />
            </div>
          </div>
        </div>
      </section>

      <section className='py-20 bg-card'>
        <div className='container mx-auto px-6'>
          <div className='bg-card border border-border rounded-lg p-10 md:p-16 grid md:grid-cols-2 gap-12 items-center shadow-lg hover:shadow-xl transition-shadow duration-300'>
            <div
              className='text-center md:text-left opacity-0 animate-slide-in-left'
              style={{ animationDelay: '0.3s' }}
            >
              <h2 className='text-3xl font-bold text-foreground mb-4 font-heading'>Support Our Mission</h2>
              <p className='text-muted-foreground mb-8 leading-relaxed'>
                Your generous support helps us provide free workshops, mentorship, and resources to aspiring tech
                professionals in our community. Together, we can build a brighter digital future.
              </p>
              <Link
                href='/support'
                className='bg-secondary text-secondary-foreground font-bold px-8 py-3 rounded-full text-lg hover:bg-secondary/90 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-secondary/30'
              >
                Make a Donation
              </Link>
            </div>
            <div className='hidden md:block opacity-0 animate-slide-in-right' style={{ animationDelay: '0.3s' }}>
              <img
                src='https://images.unsplash.com/photo-1593113598332-cd288d649433?q=80&w=2070&auto=format&fit=crop'
                alt='Donation impact'
                className='rounded-lg object-cover w-full h-full max-h-64 transform transition-transform duration-500 hover:scale-[1.02]'
              />
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className='container mx-auto px-6 py-16 text-center'>
          <h2 className='text-3xl font-bold text-foreground mb-4 font-heading'>Ready to Start Your Tech Journey?</h2>
          <p className='max-w-2xl mx-auto mb-8 text-muted-foreground'>
            Join a vibrant community of learners, builders, and innovators. Your future in tech starts here.
          </p>
          <Link
            href='/contact'
            className='bg-primary text-primary-foreground font-bold px-8 py-4 rounded-full text-lg hover:bg-primary/90 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-primary/30 animate-bounce-subtle'
          >
            Get In Touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
